import { atom, useAtom } from "jotai";
import { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";
import financeApiService from "../../../../services/financeApiService";
import financeV2Adapters from "../../../../adapters/financeV2Adapters";
import { quotationService } from "../../../../services/quotationService";
import tools from "../../../../helpers/tools";

// ── Atoms ────────────────────────────────────────────────────────

const initialFinanceData = {
  uuid: null,
  reference: "",
  status: 1,
  // L'enveloppe pilote la demande (financeur + type en sont déduits côté API).
  enveloppe: "",
  // Dérivés, conservés pour l'affichage en-tête uniquement.
  financeType: "",
  financier: "",
  totalEnvelop: 0,
  quotations: [],
  demandeEnvoyeeLe: null,
  demandeEnvoyeePar: null,
  etapier: [],
  todos: [],
};

// Sérialise l'état éditable pour détecter des modifications non enregistrées.
// totalEnvelop est dérivé (recalculé côté client) → exclu pour éviter les faux positifs.
// todos est persisté indépendamment via basculerTodo (PATCH immédiat) → exclu
// pour ne pas déclencher un faux « non enregistré » sur simple coche/décoche.
export const serializeFinanceForDirty = (data) => {
  const { totalEnvelop, todos, ...rest } = data || {};
  return JSON.stringify(rest);
};

export const financeDataAtom = atom(initialFinanceData);
// Empreinte du dernier état sauvegardé/chargé : référence du flag « dirty ».
export const savedSnapshotAtom = atom(
  serializeFinanceForDirty(initialFinanceData),
);
// Vrai dès que le formulaire diffère du dernier état enregistré.
export const financeDirtyAtom = atom(
  (get) =>
    serializeFinanceForDirty(get(financeDataAtom)) !== get(savedSnapshotAtom),
);
// Nombre de périodes par devis tel que persisté au dernier chargement/enregistrement.
// Sert à détecter un ajout/suppression de période pas encore sauvegardé via le bouton
// « Enregistrer » principal (l'ajout/suppression de période est un calcul stateless
// côté API tant qu'on ne sauvegarde pas), pour ne pas ouvrir la saisie de l'échéancier
// sur une période qui n'existe pas encore côté serveur.
export const savedPeriodsCountAtom = atom({});
const computeSavedPeriodsCount = (data) =>
  Object.fromEntries(
    (data.quotations || []).map((q) => [q.quotation, q.periods.length]),
  );
export const financeLoadingAtom = atom(false);
export const financeCalculatingAtom = atom(false);
export const financierListAtom = atom([]);
export const quotationDataAtom = atom([]);
// Devis rattachés à la demande courante (édition) — distincts du pool de recherche.
export const demandeQuotationsAtom = atom([]);
export const quotationSearchLoadingAtom = atom(false);
export const selectedTabAtom = atom(0);

// LS-640 — codes d'erreur 422 récupérables par la saisie d'un taux VR manuel :
// une grille sans correspondance pour la durée/km (cas ciblé par LS-640) et une
// grille totalement absente pour le véhicule (grille_vr_introuvable, préexistant)
// sont tous deux court-circuités par `taux_vr_manuel` côté API (CalculerVRDepuisTableau
// l'applique avant même de tester si la grille est vide).
const TAUX_VR_MANUEL_RECOVERABLE_CODES = new Set([
  "taux_vr_introuvable",
  "grille_vr_introuvable",
]);

// LS-673 — la VR se saisit sur tous les paliers via le couple valeur + unité :
// `intermediateResidualValue` (override brut réservé aux paliers intermédiaires)
// n'existe plus.
const RECALC_FIELDS = new Set(["dBegin", "duration", "residualValue"]);

// LS-683 — les champs de saisie VR (montant et taux, LS-670/LS-673) ne
// recalculent plus pendant la frappe : le recalcul grisait le champ et coupait
// une saisie à plusieurs chiffres/décimales (ex. « 7789,25 »). La modification
// est mémorisée ici et le recalcul part à la sortie du champ, via
// commitPeriodField appelé au blur par FinanceTabV2.
const BLUR_RECALC_FIELDS = new Set([
  "vrUnite",
  "vrValeurSaisie",
  "tauxVrManuel",
]);

// ── Hook ─────────────────────────────────────────────────────────

const useFinanceDetail = () => {
  const [financeData, setFinanceData] = useAtom(financeDataAtom);
  const [isLoading, setIsLoading] = useAtom(financeLoadingAtom);
  const [isCalculating, setIsCalculating] = useAtom(financeCalculatingAtom);
  const [financierList] = useAtom(financierListAtom);
  const [quotationData, setQuotationData] = useAtom(quotationDataAtom);
  const [demandeQuotations, setDemandeQuotations] = useAtom(
    demandeQuotationsAtom,
  );
  const [isSearchingQuotations, setIsSearchingQuotations] = useAtom(
    quotationSearchLoadingAtom,
  );
  const [selectedTab, setSelectedTab] = useAtom(selectedTabAtom);
  const [, setSavedSnapshot] = useAtom(savedSnapshotAtom);
  const [isDirty] = useAtom(financeDirtyAtom);
  const [savedPeriodsCount, setSavedPeriodsCount] = useAtom(
    savedPeriodsCountAtom,
  );

  const debounceRef = useRef(null);
  // LS-683 — saisie VR en attente de recalcul : { quotationIndex, periodIndex,
  // field }, consommée par commitPeriodField au blur. Une seule à la fois : le
  // focus quitte forcément un champ VR avant d'en modifier un autre.
  const pendingVrCommitRef = useRef(null);
  // Numéro de la dernière requête recalculer-periodes émise, par quotationIndex.
  // Le debounce n'annule que le timer en attente, jamais une requête déjà en vol :
  // si une réponse plus ancienne revient après une réponse plus récente, elle ne
  // doit pas écraser l'état avec des données périmées (ex: VR resaisie pendant
  // qu'un appel précédent est toujours en cours).
  const recalculRequestVersionRef = useRef({});

  // LS-640 — prompt de saisie du taux VR manuel, ouvert quand l'API renvoie le
  // code `taux_vr_introuvable` (grille sans correspondance pour la durée/km
  // d'un palier). { quotationIndex, periodIndex, onSubmit(tauxVrManuel) }.
  const [tauxVrManuelPrompt, setTauxVrManuelPrompt] = useState(null);

  const submitTauxVrManuel = useCallback(
    (tauxVrManuel) => {
      const prompt = tauxVrManuelPrompt;
      if (!prompt) return;
      setTauxVrManuelPrompt(null);
      prompt.onSubmit(tauxVrManuel);
    },
    [tauxVrManuelPrompt],
  );

  const cancelTauxVrManuel = useCallback(() => {
    setTauxVrManuelPrompt(null);
  }, []);

  // ── Helpers ──────────────────────────────────────────────────

  const updateFinanceField = useCallback(
    (key, value) => {
      setFinanceData((prev) => ({ ...prev, [key]: value }));
    },
    [setFinanceData],
  );

  const updateQuotationPeriods = useCallback(
    (quotationIndex, newPeriods) => {
      setFinanceData((prev) => {
        const quotations = [...prev.quotations];
        quotations[quotationIndex] = {
          ...quotations[quotationIndex],
          periods: newPeriods,
        };
        return { ...prev, quotations };
      });
    },
    [setFinanceData],
  );

  const updateQuotationFromBloc = useCallback(
    (quotationIndex, bloc, { removedIndex = null } = {}) => {
      const converted = financeV2Adapters.fromApi.blocDevis(bloc);
      setFinanceData((prev) => {
        const quotations = [...prev.quotations];
        const oldPeriods = quotations[quotationIndex]?.periods || [];
        // Réinjecte les valeurs purement locales que les routes de calcul ne
        // renvoient pas : mensualité retenue (LS-612) et saisie VR par palier
        // (LS-673), avec décalage d'index si une période a été supprimée.
        const periods = financeV2Adapters.fusionnerPeriodesRecalculees(
          oldPeriods,
          converted.periods,
          { removedIndex },
        );
        quotations[quotationIndex] = {
          ...quotations[quotationIndex],
          periods,
          residualValue: converted.residualValue,
        };
        return { ...prev, quotations };
      });
    },
    [setFinanceData],
  );

  // Recherche des devis validés, stockée dans les atoms du module : FinanceV2
  // ne touche plus à la tranche partagée state.quotation (qui porte le devis
  // ouvert sous la modale).
  const searchValidQuotations = useCallback(
    async (filters = {}) => {
      setIsSearchingQuotations(true);
      try {
        const search = tools.getSearchString({
          clientName: filters.clientName,
          client: filters.client,
          catalogs: filters.catalogs,
          reference: filters.reference,
          brand: filters.brand,
          brandModel: filters.brandModel,
          userGroups: filters.userGroups,
          quotationDetails: filters.quotationDetails,
          page: filters.page,
        });
        const res = await quotationService.getValidQuotations(search);
        setQuotationData(res.data);
      } catch (err) {
        console.error("Erreur recherche devis validés", err);
        toast.error("Erreur lors de la recherche des devis validés.");
      } finally {
        setIsSearchingQuotations(false);
      }
    },
    [setQuotationData, setIsSearchingQuotations],
  );

  // Charge UNIQUEMENT les devis rattachés à la demande (mode restrictif de
  // l'API via demandeUuid). Sert à résoudre les onglets « Périodes ».
  const loadDemandeQuotations = useCallback(
    async (demandeUuid) => {
      if (!demandeUuid) return;
      try {
        const search = tools.getSearchString({
          demandeUuid,
        });
        const res = await quotationService.getValidQuotations(search);
        setDemandeQuotations(res.data);
      } catch (err) {
        console.error("Erreur chargement devis de la demande", err);
        toast.error("Erreur lors du chargement des devis de la demande.");
      }
    },
    [setDemandeQuotations],
  );

  // ── Enveloppe ────────────────────────────────────────────────

  const refreshEnvelop = useCallback(
    async (quotations) => {
      const devisUuids = quotations.map((q) => q.quotation).filter(Boolean);
      if (devisUuids.length === 0) {
        updateFinanceField("totalEnvelop", 0);
        return 0;
      }
      try {
        const res = await financeApiService.calculateEnvelop(devisUuids);
        const enveloppe = financeV2Adapters.fromApi.enveloppe(res.data);
        updateFinanceField("totalEnvelop", enveloppe.totalEnvelop);
        return enveloppe.totalEnvelop;
      } catch (err) {
        console.error("Erreur calcul enveloppe", err);
        toast.error("Erreur lors du calcul de l'enveloppe.");
        return 0;
      }
    },
    [updateFinanceField],
  );

  // ── Select / unselect quotation ──────────────────────────────

  const selectQuotation = useCallback(
    async (quotationObj) => {
      setIsCalculating(true);
      try {
        const res = await financeApiService.initializeDevis(quotationObj.uuid);
        const bloc = financeV2Adapters.fromApi.blocDevis(res.data);

        setFinanceData((prev) => {
          const newQuotations = [
            ...prev.quotations,
            {
              quotation: quotationObj.uuid,
              reference: bloc.reference || quotationObj.reference,
              periods: bloc.periods,
              residualValue: bloc.residualValue,
            },
          ];
          refreshEnvelop(newQuotations);
          return { ...prev, quotations: newQuotations };
        });
      } catch (err) {
        console.error("Erreur initialisation devis", err);
        toast.error("Erreur lors de l'initialisation du devis.");
      } finally {
        setIsCalculating(false);
      }
    },
    [setFinanceData, setIsCalculating, refreshEnvelop],
  );

  const unselectQuotation = useCallback(
    (quotationUuid) => {
      setFinanceData((prev) => {
        const newQuotations = prev.quotations.filter(
          (q) => q.quotation !== quotationUuid,
        );
        refreshEnvelop(newQuotations);
        return { ...prev, quotations: newQuotations };
      });
    },
    [setFinanceData, refreshEnvelop],
  );

  // ── Period field update ──────────────────────────────────────

  // manualRates : { [periodIndex]: tauxVrManuel } — surcharge appliquée en plus
  // de l'état courant, utilisée pour rejouer l'appel après saisie du taux
  // manuel (LS-640) sans attendre un nouvel edit utilisateur.
  const performRecalcul = useCallback(
    (quotationIndex, periodIndex, field, manualRates = {}) => {
      setIsCalculating(true);
      const version =
        (recalculRequestVersionRef.current[quotationIndex] || 0) + 1;
      recalculRequestVersionRef.current[quotationIndex] = version;
      const isStale = () =>
        recalculRequestVersionRef.current[quotationIndex] !== version;

      setFinanceData((prev) => {
        const q = prev.quotations[quotationIndex];
        const hasManualRates = Object.keys(manualRates).length > 0;
        const currentPeriods = hasManualRates
          ? q.periods.map((p, i) =>
              manualRates[i] !== undefined
                ? { ...p, tauxVrManuel: manualRates[i] }
                : p,
            )
          : q.periods;

        const payload = financeV2Adapters.toApi.recalculerPayload(
          q.quotation,
          periodIndex,
          field,
          currentPeriods,
        );

        financeApiService
          .recalculerPeriodes(payload)
          .then((res) => {
            // Une requête plus récente a déjà été émise entre-temps : cette
            // réponse est périmée, elle ne doit pas écraser un edit plus frais.
            if (isStale()) return;
            updateQuotationFromBloc(quotationIndex, res.data);
          })
          .catch((err) => {
            if (isStale()) return;
            // LS-640 — la grille VR n'a pas de taux exploitable pour la
            // période éditée (durée/km sans correspondance, ou grille
            // totalement absente pour le véhicule) : propose une saisie
            // manuelle du taux plutôt qu'un échec sec. Ne re-propose pas si
            // un taux vient déjà d'être essayé pour cette période (évite une
            // boucle de prompts).
            if (
              TAUX_VR_MANUEL_RECOVERABLE_CODES.has(err?.response?.data?.code) &&
              manualRates[periodIndex] === undefined
            ) {
              setTauxVrManuelPrompt({
                quotationIndex,
                periodIndex,
                message: err?.response?.data?.message,
                onSubmit: (taux) =>
                  performRecalcul(quotationIndex, periodIndex, field, {
                    ...manualRates,
                    [periodIndex]: taux,
                  }),
              });
              return;
            }
            console.error("Erreur recalcul périodes", err);
            toast.error(
              err?.response?.data?.message ||
                "Erreur lors du recalcul des périodes.",
            );
          })
          .finally(() => {
            if (isStale()) return;
            setIsCalculating(false);
          });

        return prev;
      });
    },
    [setFinanceData, setIsCalculating, updateQuotationFromBloc],
  );

  const updatePeriodField = useCallback(
    (quotationIndex, periodIndex, field, value) => {
      setFinanceData((prev) => {
        const quotations = [...prev.quotations];
        const q = { ...quotations[quotationIndex] };
        const periods = q.periods.map((p) => ({ ...p }));
        periods[periodIndex] = { ...periods[periodIndex], [field]: value };
        q.periods = periods;
        quotations[quotationIndex] = q;
        return { ...prev, quotations };
      });

      if (BLUR_RECALC_FIELDS.has(field)) {
        // Champ vidé : la saisie en attente est annulée, comme le garde-fou
        // valeur vide ci-dessous — un couple incomplet ne doit jamais partir
        // au recalcul.
        pendingVrCommitRef.current =
          value === "" ? null : { quotationIndex, periodIndex, field };
        return;
      }

      if (!RECALC_FIELDS.has(field)) return;

      if (debounceRef.current) clearTimeout(debounceRef.current);

      // Un champ vidé en cours de saisie (ex: durée effacée avant d'y retaper une
      // valeur) ne doit jamais déclencher d'appel API : une durée nulle transmise
      // au recalcul décale la durée cumulée en cascade et peut produire des
      // mensualités négatives, bloquant ensuite toute modification (422 côté API).
      // On annule simplement l'appel en attente ; il sera re-planifié dès que
      // l'utilisateur saisit une valeur valide.
      if (value === "") return;

      debounceRef.current = setTimeout(() => {
        performRecalcul(quotationIndex, periodIndex, field);
      }, 400);
    },
    [setFinanceData, performRecalcul],
  );

  // LS-683 — déclenche le recalcul de la saisie VR en attente à la sortie du
  // champ (blur). Ne fait rien si aucun champ VR n'a été modifié (simple
  // traversée en tabulant) ou si le blur vient d'un autre palier que celui de
  // la saisie — la saisie reste alors en attente du blur de son propre champ.
  const commitPeriodField = useCallback(
    (quotationIndex, periodIndex) => {
      const pending = pendingVrCommitRef.current;
      if (
        !pending ||
        pending.quotationIndex !== quotationIndex ||
        pending.periodIndex !== periodIndex
      ) {
        return;
      }
      pendingVrCommitRef.current = null;
      performRecalcul(quotationIndex, periodIndex, pending.field);
    },
    [performRecalcul],
  );

  // ── Add / Remove period ──────────────────────────────────────

  // manualRates : { [periodIndex]: tauxVrManuel } — cf. performRecalcul (LS-640).
  const addPeriod = useCallback(
    async (quotationIndex, manualRates = {}) => {
      const q = financeData.quotations[quotationIndex];
      if (!q) return;

      const lastIndex = q.periods.length - 1;
      const lastPeriod = q.periods[lastIndex];
      if (
        lastPeriod &&
        (lastPeriod.duration === "" || lastPeriod.duration === 0)
      ) {
        toast.warning(
          "Veuillez d'abord renseigner une durée pour la période précédente.",
        );
        return;
      }

      const hasManualRates = Object.keys(manualRates).length > 0;
      const periods = hasManualRates
        ? q.periods.map((p, i) =>
            manualRates[i] !== undefined ? { ...p, tauxVrManuel: manualRates[i] } : p,
          )
        : q.periods;

      setIsCalculating(true);
      try {
        const payload = financeV2Adapters.toApi.ajouterPeriodePayload(
          q.quotation,
          periods,
        );
        const res = await financeApiService.ajouterPeriode(payload);
        updateQuotationFromBloc(quotationIndex, res.data);
      } catch (err) {
        // LS-640 — la grille VR n'a pas de taux exploitable pour l'ancienne
        // dernière période (qui devient intermédiaire en ajoutant celle-ci) :
        // durée/km sans correspondance, ou grille totalement absente pour le
        // véhicule. Propose une saisie manuelle du taux plutôt qu'un échec sec.
        if (
          TAUX_VR_MANUEL_RECOVERABLE_CODES.has(err?.response?.data?.code) &&
          manualRates[lastIndex] === undefined
        ) {
          setTauxVrManuelPrompt({
            quotationIndex,
            periodIndex: lastIndex,
            message: err?.response?.data?.message,
            onSubmit: (taux) =>
              addPeriod(quotationIndex, { ...manualRates, [lastIndex]: taux }),
          });
          return;
        }
        console.error("Erreur ajout période", err);
        toast.error(
          err?.response?.data?.message ||
            "Erreur lors de l'ajout de la période.",
        );
      } finally {
        setIsCalculating(false);
      }
    },
    [financeData.quotations, setIsCalculating, updateQuotationFromBloc],
  );

  const removePeriod = useCallback(
    async (quotationIndex, periodIndex) => {
      const q = financeData.quotations[quotationIndex];
      if (!q) return;

      setIsCalculating(true);
      try {
        const payload = financeV2Adapters.toApi.supprimerPeriodePayload(
          q.quotation,
          periodIndex,
          q.periods,
        );
        const res = await financeApiService.supprimerPeriode(payload);
        updateQuotationFromBloc(quotationIndex, res.data, {
          removedIndex: periodIndex,
        });
      } catch (err) {
        console.error("Erreur suppression période", err);
        toast.error("Erreur lors de la suppression de la période.");
      } finally {
        setIsCalculating(false);
      }
    },
    [financeData.quotations, setIsCalculating, updateQuotationFromBloc],
  );

  // ── Saisie manuelle de l'échéancier (LS-612) ──────────────────

  // Persistance immédiate (comme basculerTodo) : indépendante du bouton
  // Enregistrer, disponible même dossier verrouillé (Dossier complet+).
  // Remplace l'état complet par la réponse serveur (recalage de dates en
  // cascade inclus) et resynchronise le snapshot pour ne pas déclencher un
  // faux « non enregistré » sur une valeur déjà persistée côté API.
  const saisirMensualiteRetenue = useCallback(
    async (quotationIndex, numero, { mensualiteRetenue, dateDebut }) => {
      const q = financeData.quotations[quotationIndex];
      if (!q) return false;
      try {
        const payload = financeV2Adapters.toApi.saisirMensualiteRetenuePayload(
          mensualiteRetenue,
          dateDebut,
        );
        const res = await financeApiService.saisirMensualiteRetenue(
          financeData.uuid,
          q.quotation,
          numero,
          payload,
        );
        const data = financeV2Adapters.fromApi.demande(res.data);
        setFinanceData((prev) => ({ ...data, totalEnvelop: prev.totalEnvelop }));
        setSavedSnapshot(serializeFinanceForDirty(data));
        setSavedPeriodsCount(computeSavedPeriodsCount(data));
        return true;
      } catch (err) {
        console.error("Erreur saisie mensualité retenue", err);
        toast.error(
          err?.response?.data?.message ||
            "Erreur lors de la saisie de la mensualité retenue.",
        );
        return false;
      }
    },
    [
      financeData.uuid,
      financeData.quotations,
      setFinanceData,
      setSavedSnapshot,
      setSavedPeriodsCount,
    ],
  );

  // ── Financier change ─────────────────────────────────────────

  // Le financeur et le type de financement sont désormais déduits de
  // l'enveloppe côté API : on ne stocke que l'enveloppe sélectionnée.
  const changeEnveloppe = useCallback(
    (enveloppeUuid) => {
      updateFinanceField("enveloppe", enveloppeUuid);
    },
    [updateFinanceField],
  );

  // ── CRUD ─────────────────────────────────────────────────────

  const loadDemande = useCallback(
    async (uuid) => {
      setIsLoading(true);
      try {
        const res = await financeApiService.getDemande(uuid);
        const data = financeV2Adapters.fromApi.demande(res.data);
        setFinanceData(data);
        setSavedSnapshot(serializeFinanceForDirty(data));
        setSavedPeriodsCount(computeSavedPeriodsCount(data));

        // L'API ne renvoie pas enveloppe_total : on la recalcule après chargement.
        await refreshEnvelop(data.quotations);
      } catch (err) {
        console.error("Erreur chargement demande", err);
        toast.error("Erreur lors du chargement de la demande.");
      } finally {
        setIsLoading(false);
      }
    },
    [
      setIsLoading,
      setFinanceData,
      setSavedSnapshot,
      setSavedPeriodsCount,
      refreshEnvelop,
    ],
  );

  // Demande de financement déjà liée à un devis ? (route API à venir)
  // Retourne l'uuid chargé, ou null si aucune (404, route absente ou erreur).
  const loadDemandeFromDevis = useCallback(
    async (devisUuid) => {
      try {
        const res = await financeApiService.getDemandeByDevis(devisUuid);
        const existing = res.data?.data?.[0];
        if (!existing?.uuid) return null;
        await loadDemande(existing.uuid);
        return existing.uuid;
      } catch (err) {
        console.error("Erreur recherche demande liée au devis", err);
        toast.error(
          "Impossible de vérifier le financement lié à ce devis — création d'une nouvelle demande."
        );
        return null;
      }
    },
    [loadDemande],
  );

  const saveDemande = useCallback(async () => {
    if (!financeData.enveloppe) {
      toast.warning("Veuillez sélectionner une enveloppe avant d'enregistrer.");
      return null;
    }
    setIsLoading(true);
    try {
      const payload = financeV2Adapters.toApi.demande(financeData);
      let res;
      if (financeData.uuid) {
        res = await financeApiService.updateDemande(financeData.uuid, payload);
        toast.success("Le financement a été correctement mis à jour.");
      } else {
        res = await financeApiService.createDemande(payload);
        toast.success("Le financement a été créé.");
      }
      const data = financeV2Adapters.fromApi.demande(res.data);
      // La réponse ne contient pas enveloppe_total : on conserve celle déjà calculée.
      setFinanceData((prev) => ({ ...data, totalEnvelop: prev.totalEnvelop }));
      setSavedSnapshot(serializeFinanceForDirty(data));
      setSavedPeriodsCount(computeSavedPeriodsCount(data));
      return data.uuid;
    } catch (err) {
      console.error("Erreur sauvegarde demande", err);
      const msg =
        err?.response?.data?.message || "Erreur lors de la sauvegarde.";
      toast.error(msg);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [
    financeData,
    setIsLoading,
    setFinanceData,
    setSavedSnapshot,
    setSavedPeriodsCount,
  ]);

  // Coche/décoche un todo de la checklist courante — toujours autorisé, même
  // verrouillée. Optimiste sur `financeData.todos`, resynchronisé sur erreur.
  const basculerTodo = useCallback(
    async (todoUuid, coche) => {
      const previous = financeData.todos;
      setFinanceData((prev) => ({
        ...prev,
        todos: prev.todos.map((t) =>
          t.uuid === todoUuid ? { ...t, coche } : t,
        ),
      }));
      try {
        const res = await financeApiService.basculerTodoDemande(
          financeData.uuid,
          todoUuid,
          coche,
        );
        const data = financeV2Adapters.fromApi.demande(res.data);
        setFinanceData((prev) => ({ ...prev, todos: data.todos }));
      } catch (err) {
        console.error("Erreur bascule todo", err);
        toast.error(
          err?.response?.data?.message ||
            "Erreur lors de la mise à jour du todo.",
        );
        setFinanceData((prev) => ({ ...prev, todos: previous }));
      }
    },
    [financeData.uuid, financeData.todos, setFinanceData],
  );

  // Modale de confirmation des transitions de statut (remplace window.confirm()).
  // askConfirmation ouvre la modale et retourne une promesse résolue par le
  // clic de l'utilisateur, portée par pendingConfirmRef.
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    message: "",
    items: [],
  });
  const pendingConfirmRef = useRef(null);

  const askConfirmation = useCallback((message, items = []) => {
    return new Promise((resolve) => {
      pendingConfirmRef.current = resolve;
      setConfirmState({ isOpen: true, message, items });
    });
  }, []);

  const resolveConfirmation = useCallback((confirmed) => {
    setConfirmState({ isOpen: false, message: "", items: [] });
    pendingConfirmRef.current?.(confirmed);
    pendingConfirmRef.current = null;
  }, []);

  const confirmTransition = useCallback(() => resolveConfirmation(true), [resolveConfirmation]);
  const cancelTransition = useCallback(() => resolveConfirmation(false), [resolveConfirmation]);

  // Récapitulatif des todos de l'étape courante (cochés/non cochés) — les
  // étapes passées devenant irréversibles, ce message est la dernière
  // occasion de le mettre sous les yeux de l'utilisateur avant de basculer.
  const checklistEtapeCouranteNote = useCallback(() => {
    const todosEtape = financeData.todos.filter(
      (t) => t.statut === financeData.status,
    );
    if (todosEtape.length === 0) return "";
    const nonCoches = todosEtape.filter((t) => !t.coche).map((t) => t.libelle);
    const coches = todosEtape.length - nonCoches.length;
    let note = ` Checklist de l'étape actuelle : ${coches}/${todosEtape.length} coché(s).`;
    if (nonCoches.length > 0) {
      note += ` Non coché(s) : ${nonCoches.join(", ")}.`;
    }
    return note;
  }, [financeData.todos, financeData.status]);

  // Exécute une transition de statut : modale de confirmation → POST 204 → reload.
  // Recharge la demande pour récupérer l'état serveur (statut suivant) au
  // lieu de parser une réponse vide.
  const runTransition = useCallback(
    async (apiCall, confirmMessage, successMessage, errorMessage, confirmItems = []) => {
      const confirmed = await askConfirmation(
        confirmMessage + checklistEtapeCouranteNote(),
        confirmItems,
      );
      if (!confirmed) {
        return null;
      }
      setIsLoading(true);
      try {
        await apiCall(financeData.uuid);
        toast.success(successMessage);
        await loadDemande(financeData.uuid);
        return financeData.uuid;
      } catch (err) {
        console.error(errorMessage, err);
        toast.error(err?.response?.data?.message || errorMessage);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [
      financeData.uuid,
      setIsLoading,
      loadDemande,
      askConfirmation,
      checklistEtapeCouranteNote,
    ],
  );

  const envoyerDemande = useCallback(
    (confirmItems) =>
      runTransition(
        financeApiService.envoyerDemande,
        "Marquer cette demande comme envoyée ? Aucun e-mail ne sera envoyé au financeur — à utiliser si vous l'avez déjà transmise vous-même.",
        "La demande a été marquée comme envoyée.",
        "Erreur lors du passage à « Demande envoyée ».",
        confirmItems,
      ),
    [runTransition],
  );

  const accordRecuDemande = useCallback(
    (confirmItems) =>
      runTransition(
        financeApiService.accordRecuDemande,
        "Marquer l'accord du financeur comme reçu ?",
        "L'accord du financeur a été enregistré.",
        "Erreur lors du passage à « Accord reçu ».",
        confirmItems,
      ),
    [runTransition],
  );

  const dossierCompletDemande = useCallback(
    (confirmItems) =>
      runTransition(
        financeApiService.dossierCompletDemande,
        "Marquer le dossier comme complet ? La demande sera verrouillée pour les commerciaux.",
        "Le dossier a été marqué comme complet.",
        "Erreur lors du passage à « Dossier complet ».",
        confirmItems,
      ),
    [runTransition],
  );

  const dossierTransmisDemande = useCallback(
    (confirmItems) =>
      runTransition(
        financeApiService.dossierTransmisDemande,
        "Marquer le dossier comme transmis au financeur ?",
        "Le dossier a été marqué comme transmis.",
        "Erreur lors du passage à « Dossier transmis ».",
        confirmItems,
      ),
    [runTransition],
  );

  const transmisEtSigneDemande = useCallback(
    (confirmItems) =>
      runTransition(
        financeApiService.transmisEtSigneDemande,
        "Marquer le financement comme transmis et signé ?",
        "Le financement a été marqué comme transmis et signé.",
        "Erreur lors du passage à « Transmis et signé ».",
        confirmItems,
      ),
    [runTransition],
  );

  // LS-629 — depuis n'importe quel statut, définitif : détache les devis rattachés et
  // libère l'enveloppe. La demande devient non modifiable pour tout le monde.
  const abandonnerDemande = useCallback(
    () =>
      runTransition(
        financeApiService.abandonnerDemande,
        "Abandonner définitivement cette demande ? Les devis rattachés seront détachés et redeviendront finançables, le montant réservé/engagé sur l'enveloppe sera libéré. Cette action est irréversible.",
        "La demande a été abandonnée.",
        "Erreur lors de l'abandon de la demande.",
      ),
    [runTransition],
  );

  // LS-625 — envoi (ou renvoi) par email au financeur : pas de modale de
  // confirmation générique (la modale dédiée porte déjà la validation),
  // juste l'appel API + toast + rechargement de la demande.
  const envoyerAuFinanceur = useCallback(
    async (payload) => {
      setIsLoading(true);
      try {
        await financeApiService.envoyerAuFinanceur(
          financeData.uuid,
          financeV2Adapters.toApi.envoyerAuFinanceur(payload),
        );
        toast.success("La demande a été envoyée au financeur.");
        await loadDemande(financeData.uuid);
        return true;
      } catch (err) {
        console.error("Erreur envoi au financeur", err);
        toast.error(
          err?.response?.data?.message ||
            "Erreur lors de l'envoi au financeur.",
        );
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [financeData.uuid, setIsLoading, loadDemande],
  );

  // ── Reset ────────────────────────────────────────────────────

  const resetFinance = useCallback(() => {
    setFinanceData(initialFinanceData);
    setSavedSnapshot(serializeFinanceForDirty(initialFinanceData));
    setSavedPeriodsCount({});
    setSelectedTab(0);
    setQuotationData([]);
    setDemandeQuotations([]);
    setIsSearchingQuotations(false);
    // Les atoms survivent à l'unmount : une fermeture de modale en plein
    // chargement ne doit pas laisser un loading bloqué à la réouverture.
    setIsLoading(false);
    setIsCalculating(false);
  }, [
    setFinanceData,
    setSavedSnapshot,
    setSavedPeriodsCount,
    setSelectedTab,
    setQuotationData,
    setDemandeQuotations,
    setIsSearchingQuotations,
    setIsLoading,
    setIsCalculating,
  ]);

  // ── Return ───────────────────────────────────────────────────

  return {
    financeData,
    setFinanceData,
    isDirty,
    savedPeriodsCount,
    isLoading,
    isCalculating,
    financierList,
    quotationData,
    demandeQuotations,
    setQuotationData,
    isSearchingQuotations,
    searchValidQuotations,
    loadDemandeQuotations,
    selectedTab,
    setSelectedTab,

    updateFinanceField,
    updateQuotationPeriods,

    selectQuotation,
    unselectQuotation,
    updatePeriodField,
    commitPeriodField,
    addPeriod,
    removePeriod,
    changeEnveloppe,
    saisirMensualiteRetenue,

    loadDemande,
    loadDemandeFromDevis,
    saveDemande,
    envoyerDemande,
    envoyerAuFinanceur,
    accordRecuDemande,
    dossierCompletDemande,
    dossierTransmisDemande,
    transmisEtSigneDemande,
    abandonnerDemande,
    basculerTodo,
    confirmState,
    confirmTransition,
    cancelTransition,
    resetFinance,
    refreshEnvelop,
    tauxVrManuelPrompt,
    submitTauxVrManuel,
    cancelTauxVrManuel,
  };
};

export default useFinanceDetail;
